import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { BsshDatePipe } from '@app/core/utilities/pipes/bssh-date.pipe';
import { BytesPipe } from '@app/core/utilities/pipes/bytes.pipe';
import { RadLinkRendererComponent } from '@app/shared/cell-renderers/rad-link-renderer/rad-link-renderer.component';
import { get } from 'lodash';
import { ColumnMinWidth, OverrideColumnDefinition, OverrideColumnParameters, SearchGridOptionsFormatter } from './search-grid-options-formatter';
import { DataTableUtilities } from '@app/core/utilities/data-table-utilities';

export enum ProjectSearchColumnFields {
    Name = 'Name',
    DateUpdated = 'Updated',
    Owner = 'Owner',
    Size = 'Size'
}

export class ProjectSearchGridOptionsFormatter extends SearchGridOptionsFormatter<ProjectSearchColumnFields> {

    constructor(
        public searchResourceDictionaryService: SearchResourceDictionaryService,
        public bytePipe: BytesPipe,
        public bsshDatePipe: BsshDatePipe
    ) {
        super(searchResourceDictionaryService, SearchResourceType.Project);
    }

    protected getOverrideColumnParameters(): OverrideColumnParameters<ProjectSearchColumnFields> {
        const inclusiveColumns: ProjectSearchColumnFields[] = [
            ProjectSearchColumnFields.Name,
            ProjectSearchColumnFields.Owner,
            ProjectSearchColumnFields.Size,
            ProjectSearchColumnFields.DateUpdated
        ];

        const overrideColumnDefinitions: OverrideColumnDefinition<ProjectSearchColumnFields>[] = [
            {
                colId: ProjectSearchColumnFields.Name,
                headerName: 'Name',
                checkboxSelection: false
            }
        ];

        return {
            inclusiveColumns,
            overrideColumnDefinitions
        };
    }

    protected getDefaultColumnDefinitions() {
        const defaultColumnDefinitions = [
            {
                headerName: 'Project',
                field: 'Project.Name',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params: any) => {
                    if(params.data) {
                        const tooltipText = !this.isRowSelectable(params.node) ? this.getUnselectableRowMessage(params.node) : null;
                        const cellRendererParams = DataTableUtilities.getRadLinkParams(
                            params.data.Project.Name,
                            this.resourceConfig.uiHrefAccessor(params.data),
                            tooltipText
                        );
                      return cellRendererParams;
                    } else {
                      return {};
                    }
                },
                colId: ProjectSearchColumnFields.Name,
                filterActive: false,
                sortable: false,
                filter: 'customFilter',
                flex: 4.5,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Owner',
                cellRenderer: 'span',
                field: 'Project.UserOwnedBy.Name',
                colId: ProjectSearchColumnFields.Owner,
                filterActive: false,
                sortable: false,
                flex: 2,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Size',
                cellRenderer: 'span',
                valueGetter: params => {
                  if (!params.data) { return; }
                  const totalSize = get(params.data.Project, 'TotalSize', 0);
                  if (totalSize === 0) {
                    return '--';
                  }
                  return this.bytePipe.transform(totalSize, 2);
                },
                colId: ProjectSearchColumnFields.Size,
                sortable: false,
                flex: 1.5,
                minWidth: ColumnMinWidth.SIZE
            },
            {
                headerName: 'Date Updated',
                cellRenderer: 'span',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    return this.bsshDatePipe.transform(params.data.Project.DateModified);
                },
                suppressSizeToFit: false,
                colId: ProjectSearchColumnFields.DateUpdated,
                filterActive: false,
                sortable: false,
                flex: 2,
                minWidth: ColumnMinWidth.DATE
            }
        ];
        return defaultColumnDefinitions;
    }
}
